import {I18nMsg} from "@t/util/Common.ts";
import type {Component} from "vue";
import ColorPalette from "@fd/editors/html/ColorPalette.vue";

/**
 * Anatomy of toolbar
 * Toolbar is UI component which is organized into rows and groups with one or more buttons/menus.
 * T
 */
export interface Toolbar {
	segments: ToolbarSegment[]
}

export type ToolbarElementType =
	| "button"	// invoke an action
	| "switch"	// toggle on/off switch
	| "palette"	// drop down palette specified by Vue component
	| "menu";	// drop down menu with items to select

export type ToolbarComponentType =
	ToolbarElementType
	| "segment"	// toolbar segment
	| "group"	// toolbar group
	| "menuitem";	// item of the menu


export interface ToolbarComponent {
	id: string;
	type: ToolbarComponentType;
}

/**
 * Segments are pushed using space-between
 */
export interface ToolbarSegment extends ToolbarComponent {
	type: "segment";
	groups: ToolbarGroup[]
}

/**
 * Groups are separated by fixed space (8px)
 */
export interface ToolbarGroup extends ToolbarComponent {
	type: "group";
	items: ToolbarElement[];
}


export interface ToolbarElement extends ToolbarComponent {
	type: ToolbarElementType;
	icon?: string;
	title?: string;
	tooltip?: string;
	disabled?: boolean;
}

export interface HasArgument {
	arg?: any;
}

export type ToolbarMenuItems = ToolbarMenuItem[];
/** Function to asynchronously obtain menu content */
export type ToolbarMenuItemsGetter = (id?:string) => Promise<ToolbarMenuItems>;

export interface ToolbarMenuItem extends ToolbarComponent, HasArgument {
	type: "menuitem";
	icon?: string;
	title: string;
	classes?: string[];
	style?: object;
	disabled?: boolean;
	children?: ToolbarMenuItems;
}

export interface ToolbarMenu extends ToolbarElement {
	type: "menu"
	items: ToolbarMenuItems | ToolbarMenuItemsGetter
}

export interface ToolbarSwitch extends ToolbarElement {
	type: "switch",
	on?: boolean
}

/** Execute id-named action */
export interface ToolbarButton extends ToolbarElement, HasArgument {
	type: "button"
}

export interface ToolbarPalette extends ToolbarElement {
	type: "palette";
	component: string | Component;
}


function findComponentInElement(elm: ToolbarElement, id: string) {
	if (id === elm.id) return elm;
	if (elm.type === "menu") {
		const items = (elm as ToolbarMenu).items;
		if (Array.isArray(items)) {
			for (let mi of items) {
				if (mi.id === id) return mi;
			}
		}
	}
	return null;
}

function findComponentInGroup(group: ToolbarGroup, id: string):ToolbarComponent|null {
	if (id === group.id) return group;
	for (let item of group.items) {
		const found = findComponentInElement(item, id);
		if (found) return found;
	}
	return null;
}

function findComponentInSegment(seg: ToolbarSegment, id: string):ToolbarComponent|null {
	if (id === seg.id) return seg;
	for (let group of seg.groups) {
		const found = findComponentInGroup(group, id);
		if (found) return found;
	}
	return null;
}

export function findComponent(t:Toolbar, id:string):ToolbarComponent|null {
	for (let seg of t.segments) {
		const found = findComponentInSegment(seg, id);
		if (found) return found;
	}
	return null;
}

/** Modifies toolbar component and return true if the component should be included in the toolbar */
export type ToolbarFilter = (cp:ToolbarComponent)=>boolean;


export function buildToolbar(msg: I18nMsg, filter:ToolbarFilter):Toolbar {
	const t:Toolbar = <Toolbar>{
		segments: [
			<ToolbarSegment>{
				groups: standardGroups(msg, filter, "char_style", "block_style", "justify", "insert_list", "links", "history")
			}
		]
	};
	return t;
}


export function standardGroups(msg: I18nMsg, filter:ToolbarFilter, ...ids:string[]):ToolbarGroup[] {
	const groups:ToolbarGroup[] = [];
	for (let id of ids) {
		const group = standardGroup(msg, filter, id);
		if (group != null && group.items.length > 0 && filter(group)) groups.push(group);
	}
	return groups;
}


export function standardGroup(msg: I18nMsg, filter:ToolbarFilter, id: string): ToolbarGroup | null {
	switch (id) {
		case "block_style":
			return <ToolbarGroup>{
				id: "block_style",
				items: standardItems(msg, filter, "block_style_menu")
			};
		case "char_style":
			return <ToolbarGroup>{
				id: "char_style",
				items: standardItems(msg, filter, "bold", "italic", "underline", "inlineStyleMenu", "color")
			};
		case "insert_list":
			return <ToolbarGroup>{
				id: "insert_list",
				items: standardItems(msg, filter, "olist", "ulist", "dlist", "blockquote", "indent", "outdent", "hr",
					"insertTable", "tableMenu", "insertImage", "applyTemplate")
			};
		case "justify":
			return <ToolbarGroup>{
				id: "justify",
				items: standardItems(msg, filter, "justifyMenu")
			};
		case "links":
			return <ToolbarGroup>{
				id: "links",
				items: standardItems(msg, filter, "link", "unlink")
			};
		case "history":
			return <ToolbarGroup>{
				id: "history",
				items: standardItems(msg, filter, "undo", "redo")
			};
	}
	return null;
}

export function standardItems(msg: I18nMsg, filter:ToolbarFilter, ...ids:string[]):ToolbarElement[] {
	const items:ToolbarElement[] = [];
	for (let id of ids) {
		const item = standardItem(msg, filter, id);
		if (item != null && filter(item)) items.push(item);
	}
	return items;
}

function isMacOS() {
	// @ts-ignore
	return navigator.platform.toUpperCase().includes('MAC') || (navigator.userAgentData && navigator.userAgentData.platform === 'macOS');
}

const HEADING_FONT_SIZE:number[] = [26, 22, 18, 16, 15, 14];

export interface BlockTypeChange {
	node: string;
	attrs?: Record<string, any>
}

function buttonToMenuItem(btn:ToolbarButton):ToolbarMenuItem {
	const mi:ToolbarMenuItem = { ...btn, type: "menuitem", title: btn.tooltip };
	return mi;
}

function buildTableMenuItems(msg: I18nMsg, filter:ToolbarFilter):ToolbarMenuItem[] {
	const itemNames = ["insertColumnBefore", "insertColumnAfter", "insertRowBefore", "insertRowAfter", "deleteColumn", "deleteRow",
		"mergeCells", "splitCell", "toggleHeaderRow", "toggleHeaderColumn", "toggleHeaderCell", "deleteTable"];

	const items:ToolbarMenuItem[] = [];
	for (let name of itemNames) {
		const button = standardItem(msg, filter, name) as ToolbarButton;
		if (button) {
			const mi:ToolbarMenuItem = buttonToMenuItem(button);
			mi.style = { fontSize: "var(--font-size-s)" };
			if (filter(mi)) items.push(mi);
		}
	}

	// Insert nested table
	const mi = buttonToMenuItem(standardItem(msg, filter, "insertTable") as ToolbarButton);
	mi.style = { fontSize: "var(--font-size-s)" };
	mi.icon = "add-table";
	mi.id = "addTable";
	if (filter(mi)) items.push(mi);

	return items;
}


function buildCharStyleItems(msg: I18nMsg, filter:ToolbarFilter):ToolbarMenuItem[] {
	const itemNames = ["strike", "subscript", "superscript", "mark", "code", "kbd", "var", "removeFormat"];

	const items:ToolbarMenuItem[] = [];
	for (let name of itemNames) {
		const button = standardItem(msg, filter, name) as ToolbarButton;
		if (button) {
			const mi:ToolbarMenuItem = buttonToMenuItem(button);
			mi.style = { fontSize: "var(--font-size-s)" };
			if (filter(mi)) items.push(mi);
		}
	}
	return items;
}


function buildJustifyItems(msg: I18nMsg, filter:ToolbarFilter):ToolbarMenuItem[] {
	const itemNames = ["justifyLeft", "justifyCenter", "justifyRight", "justifyFull"];

	const items:ToolbarMenuItem[] = [];
	for (let name of itemNames) {
		const button = standardItem(msg, filter, name) as ToolbarButton;
		if (button) {
			const mi:ToolbarMenuItem = buttonToMenuItem(button);
			mi.style = { fontSize: "var(--font-size-s)" };
			if (filter(mi)) items.push(mi);
		}
	}
	return items;
}


function buildBlockStyleItems(msg: I18nMsg, filter:ToolbarFilter):ToolbarMenuItem[] {
	const items:ToolbarMenuItem[] = [];
	const para:ToolbarMenuItem = {
		id: "p",
		type: "menuitem",
		title: msg("block.style.text"),
		arg: <BlockTypeChange>{ node: "paragraph" }
	};
	if (filter(para)) items.push(para);

	const code:ToolbarMenuItem = {
		id: "pre",
		type: "menuitem",
		title: msg("block.style.code"),
		style: { fontFamily: "var(--font-mono)" },
		arg: <BlockTypeChange>{ node: "code_block" }
	};
	if (filter(code)) items.push(code);

	for (let i = 1; i <= 6; i++) {
		const item:ToolbarMenuItem = {
			id: "h" + i,
			type: "menuitem",
			title: msg("block.style.heading", i.toString()),
			style: { fontSize: HEADING_FONT_SIZE[i-1]+"px", fontWeight: "bold" },
			arg: <BlockTypeChange>{ node: "heading", attrs: { level: i } }
		};
		if (filter(item)) items.push(item);
	}

	const term:ToolbarMenuItem = {
		id: "dt",
		type: "menuitem",
		title: msg("block.style.dt"),
		style: { fontWeight: "bold" },
		arg: <BlockTypeChange>{ node: "dt" }
	};
	if (filter(term)) items.push(term);
	const desc:ToolbarMenuItem = {
		id: "dd",
		type: "menuitem",
		title: msg("block.style.dd"),
		arg: <BlockTypeChange>{ node: "dd" }
	};
	if (filter(desc)) items.push(desc);

	return items;
}

export function standardItem(msg: I18nMsg, filter:ToolbarFilter, id: string): ToolbarElement | null {
	let cmdKey = isMacOS() ? "⌘-" : "Ctrl-";
	let shiftKey = isMacOS() ? "⇧-" : "Shift-";

	switch (id) {
		/******* Block styles *******/
		case "block_style_menu":
			const styleMenu = <ToolbarMenu>{
				id: "block_style_menu",
				type: "menu",
				title: "Nadpis 1",
				items: buildBlockStyleItems(msg, filter)
			};
			return styleMenu;

		/******* Basic text editing *******/
		case "bold":
			return <ToolbarButton>{
				id: "bold",
				type: "button",
				icon: "edit-bold",
				tooltip: msg("editor.tooltip.bold", cmdKey+"B")
			};
		case "italic":
			return <ToolbarButton>{
				id: "italic",
				type: "button",
				icon: "edit-italic",
				tooltip: msg("editor.tooltip.italic", cmdKey+"I")
			};
		case "underline":
			return <ToolbarButton>{
				id: "underline",
				type: "button",
				icon: "edit-underline",
				tooltip: msg("editor.tooltip.underline", cmdKey+"U")
			};
		case "strike":
			return <ToolbarButton>{
				id: "strike",
				type: "button",
				icon: "edit-strike",
				tooltip: msg("editor.tooltip.strike")
			};
		case "subscript":
			return <ToolbarButton>{
				id: "subscript",
				type: "button",
				icon: "edit-subscript",
				tooltip: msg("editor.tooltip.subscript")
			};
		case "superscript":
			return <ToolbarButton>{
				id: "superscript",
				type: "button",
				icon: "edit-superscript",
				tooltip: msg("editor.tooltip.superscript")
			};
		case "mark":
			return <ToolbarButton>{
				id: "mark",
				type: "button",
				icon: "edit-marker",
				tooltip: msg("editor.tooltip.mark")
			};
		case "code":
			return <ToolbarButton>{
				id: "code",
				type: "button",
				icon: "inline-code",
				tooltip: msg("editor.tooltip.code")
			};
		case "kbd":
			return <ToolbarButton>{
				id: "kbd",
				type: "button",
				icon: "kbd",
				tooltip: msg("editor.tooltip.kbd")
			};
		case "var":
			return <ToolbarButton>{
				id: "var",
				type: "button",
				icon: "var",
				tooltip: msg("editor.tooltip.var")
			};

		case "inlineStyleMenu":
			return <ToolbarMenu>{
				id: "inlineStyleMenu",
				type: "menu",
				icon: "inline-style",
				items: buildCharStyleItems(msg, filter),
				tooltip: msg("editor.tooltip.inlineStyles")
			};

		case "color":
			return <ToolbarPalette>{
				id: "color",
				type: "palette",
				icon: "edit-fc",
				tooltip: msg("editor.tooltip.fc"),
				component: ColorPalette
			};
		case "background":
			return <ToolbarButton>{
				id: "background",
				type: "button",
				icon: "edit-bc",
				tooltip: msg("editor.tooltip.bc")
			};
		case "removeFormat":
			return <ToolbarButton>{
				id: "removeFormat",
				type: "button",
				icon: "broom",
				tooltip: msg("editor.tooltip.removeFormat")
			};
		/******* Justification *******/
		case "justifyMenu":
			return <ToolbarMenu>{
				id: "justifyMenu",
				type: "menu",
				icon: "align-center",
				items: buildJustifyItems(msg, filter),
				tooltip: msg("editor.tooltip.justifyMenu")
			};

		case "justifyLeft":
			return <ToolbarButton>{
				id: "justifyLeft",
				type: "button",
				icon: "align-left",
				tooltip: msg("editor.tooltip.alignLeft", cmdKey+shiftKey+"L")
			};
		case "justifyCenter":
			return <ToolbarButton>{
				id: "justifyCenter",
				type: "button",
				icon: "align-center",
				tooltip: msg("editor.tooltip.alignCenter", cmdKey+shiftKey+"E")
			};
		case "justifyRight":
			return <ToolbarButton>{
				id: "justifyRight",
				type: "button",
				icon: "align-right",
				tooltip: msg("editor.tooltip.alignRight", cmdKey+shiftKey+"R")
			};
		case "justifyFull":
			return <ToolbarButton>{
				id: "justifyFull",
				type: "button",
				icon: "align-justify",
				tooltip: msg("editor.tooltip.alignJustify")
			};


		/******* Insert list *******/
		case "olist":
			return <ToolbarButton>{
				id: "olist",
				type: "button",
				icon: "edit-ol",
				tooltip: msg("editor.tooltip.oList", cmdKey+shiftKey+"7")
			};
		case "ulist":
			return <ToolbarButton>{
				id: "ulist",
				type: "button",
				icon: "edit-ul",
				tooltip: msg("editor.tooltip.uList", cmdKey+shiftKey+"8")
			};
		case "dlist":
			return <ToolbarButton>{
				id: "dlist",
				type: "button",
				icon: "edit-dlist",
				tooltip: msg("editor.tooltip.dList", cmdKey+shiftKey+"8")
			};
		case "blockquote":
			return <ToolbarButton>{
				id: "blockquote",
				type: "button",
				icon: "block-quote",
				tooltip: msg("editor.tooltip.blockqoute", cmdKey+shiftKey+"8")
			};
		case "indent":
			return <ToolbarButton>{
				id: "indent",
				type: "button",
				icon: "block-indent",
				tooltip: msg("editor.tooltip.indent")
			};
		case "outdent":
			return <ToolbarButton>{
				id: "outdent",
				type: "button",
				icon: "block-outdent",
				tooltip: msg("editor.tooltip.outdent")
			};
		case "hr":
			return <ToolbarButton>{
				id: "hr",
				type: "button",
				icon: "edit-horizontal-rule",
				tooltip: msg("editor.tooltip.hr")
			};
		/******* Link/Unlink *******/
		case "link":
			return <ToolbarButton>{
				id: "link",
				type: "button",
				icon: "edit-link",
				tooltip: msg("editor.tooltip.link")
			};
		case "unlink":
			return <ToolbarButton>{
				id: "unlink",
				type: "button",
				icon: "edit-unlink",
				tooltip: msg("editor.tooltip.unlink")
			};

		/******* Templates *******/
		case "applyTemplate":
			return <ToolbarMenu>{
				id: "applyTemplate",
				type: "menu",
				icon: "insert-object",
				tooltip: msg("editor.tooltip.applyTemplate"),
				items: []
			};
		/******* Tables *******/
		case "insertTable":
			return <ToolbarButton>{
				id: "insertTable",
				type: "button",
				icon: "insert-table",
				tooltip: msg("editor.tooltip.insertTable")
			};

		case "tableMenu":
			const tableMenu = <ToolbarMenu>{
				id: "tableMenu",
				type: "menu",
				icon: "insert-table",
				items: buildTableMenuItems(msg, filter),
				tooltip: msg("editor.tooltip.tableEdit")
			};
			return tableMenu;

		case "deleteTable":
			return <ToolbarButton>{
				id: "deleteTable",
				type: "button",
				icon: "delete-table",
				tooltip: msg("editor.tooltip.tableDelete")
			};
		case "insertColumnBefore":
			return <ToolbarButton>{
				id: "insertColumnBefore",
				type: "button",
				icon: "insert-column-before",
				tooltip: msg("editor.tooltip.tableColumnBefore")
			};
		case "insertColumnAfter":
			return <ToolbarButton>{
				id: "insertColumnAfter",
				type: "button",
				icon: "insert-column-after",
				tooltip: msg("editor.tooltip.tableColumnAfter")
			};
		case "insertRowBefore":
			return <ToolbarButton>{
				id: "insertRowBefore",
				type: "button",
				icon: "insert-row-before",
				tooltip: msg("editor.tooltip.tableRowBefore")
			};
		case "insertRowAfter":
			return <ToolbarButton>{
				id: "insertRowAfter",
				type: "button",
				icon: "insert-row-after",
				tooltip: msg("editor.tooltip.tableRowAfter")
			};
		case "deleteColumn":
			return <ToolbarButton>{
				id: "deleteColumn",
				type: "button",
				icon: "delete-column",
				tooltip: msg("editor.tooltip.tableColumnDelete")
			};
		case "deleteRow":
			return <ToolbarButton>{
				id: "deleteRow",
				type: "button",
				icon: "delete-row",
				tooltip: msg("editor.tooltip.tableRowDelete")
			};
		case "mergeCells":
			return <ToolbarButton>{
				id: "mergeCells",
				type: "button",
				icon: "merge-cells",
				tooltip: msg("editor.tooltip.mergeCells")
			};
		case "splitCell":
			return <ToolbarButton>{
				id: "splitCell",
				type: "button",
				icon: "split-cells",
				tooltip: msg("editor.tooltip.splitCell")
			};
		case "toggleHeaderRow":
			return <ToolbarButton>{
				id: "toggleHeaderRow",
				type: "button",
				icon: "toggle-header-row",
				tooltip: msg("editor.tooltip.toggleHeaderRow")
			};
		case "toggleHeaderColumn":
			return <ToolbarButton>{
				id: "toggleHeaderColumn",
				type: "button",
				icon: "toggle-header-column",
				tooltip: msg("editor.tooltip.toggleHeaderColumn")
			};
		case "toggleHeaderCell":
			return <ToolbarButton>{
				id: "toggleHeaderCell",
				type: "button",
				icon: "toggle-header-cells",
				tooltip: msg("editor.tooltip.toggleHeaderCell")
			};

		/******* Images *******/
		case "insertImage":
			return <ToolbarButton>{
				id: "insertImage",
				type: "button",
				icon: "insert-image",
				tooltip: msg("editor.tooltip.insertImage")
			};
		/******* History *******/
		case "undo":
			return <ToolbarButton>{
				id: "undo",
				type: "button",
				icon: "undo",
				tooltip: msg("editor.tooltip.undo")
			};
		case "redo":
			return <ToolbarButton>{
				id: "redo",
				type: "button",
				icon: "redo",
				tooltip: msg("editor.tooltip.redo")
			};
	}
	return null;
}
